// Reusable client form (used for both create and edit)
import { useEffect, useRef, useState } from "react";
import { Globe2, Palette, Upload, Trash2, Users, Loader2, Wand2, SlidersHorizontal, PoundSterling, Activity, ClipboardCheck, Wrench, BellRing, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { shade } from "@/lib/theme";
import PortalModuleSelector from "@/components/PortalModuleSelector";
import PortalBrandingControls from "@/components/PortalBrandingControls";

const BRAND_PRESETS = [
    "#1B3A5C", "#2E7D5B", "#7A2E2E", "#9F4212", "#2A5DB0",
    "#6B4E9E", "#C68A1C", "#3F7D3A", "#B33A2E", "#1F1F1F",
];

const SECTORS = [
    "Residential / High-Rise", "Local Authority", "Property Management",
    "Demolition / Construction", "Manufacturing", "Healthcare",
    "Education", "Retail", "Office / Commercial", "Other",
];

function weekCommencingSat(dateStr) {
    if (!dateStr) return "";
    const d = new Date(`${dateStr}T00:00:00Z`);
    const daysSinceSat = (d.getUTCDay() - 6 + 7) % 7;
    d.setUTCDate(d.getUTCDate() - daysSinceSat);
    return d.toISOString().slice(0, 10);
}
const msStartWeekDisplay = (v) => v || "";

export default function ClientFormFields({ initial = {}, onChange, onPendingLogo }) {
    const [form, setForm] = useState(() => ({
        name: "", trading_name: "", sector: "", address: "",
        main_contact: "", email: "", telephone: "", website: "",
        account_manager: "", notes: "",
        portal_enabled: true, hide_financials: false, pulse_digest_enabled: false, weekly_reports_enabled: false, equipment_register_enabled: false, bsc_enabled: false,
        wsr_notifications_enabled: false, wsr_reminders_enabled: false, wsr_due_weekday: 4, wsr_due_time: "14:00", wsr_reviewer_user_ids: [], ms_reviewer_user_ids: [], ms_start_week: "",
        golden_thread_enabled: false, compliance_enabled: true,
        portal_module_mode: "preset", portal_preset_id: null, portal_modules: [],
        risk: "Medium", status: "Active",
        ...initial,
        branding: {
            primary_colour: initial?.branding?.primary_colour || "#1B3A5C",
            secondary_colour: initial?.branding?.secondary_colour ?? null,
            logo_initials: initial?.branding?.logo_initials || "",
            logo_url: initial?.branding?.logo_url ?? null,
            cover_pdf_path: initial?.branding?.cover_pdf_path ?? null,
            cover_pdf_name: initial?.branding?.cover_pdf_name ?? null,
            cover_enabled: initial?.branding?.cover_enabled ?? true,
            heading_font: initial?.branding?.heading_font ?? null,
            body_font: initial?.branding?.body_font ?? null,
            banner_url: initial?.branding?.banner_url ?? null,
            wide_logo_url: initial?.branding?.wide_logo_url ?? null,
        },
    }));

    const [catalog, setCatalog] = useState(null);
    const [presets, setPresets] = useState([]);
    const modulesTouchedRef = useRef(false);
    useEffect(() => {
        api.get("/portal-modules/catalog").then((r) => setCatalog(r.data)).catch(() => {});
        api.get("/portal-presets").then((r) => setPresets(r.data)).catch(() => {});
    }, []);

    useEffect(() => { onChange?.(form, modulesTouchedRef.current); }, [form, onChange]);

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }
    function setModuleMode(mode) { modulesTouchedRef.current = true; set("portal_module_mode", mode); }
    function setBrand(field, value) {
        setForm((f) => ({ ...f, branding: { ...f.branding, [field]: value } }));
    }

    function setModules(arr, added) {
        modulesTouchedRef.current = true;
        setForm((f) => ({ ...f, portal_modules: arr }));
        if (added?.length) toast.info("Required supporting modules have also been enabled.");
    }
    function applyPreset() {
        const p = presets.find((x) => x.id === form.portal_preset_id);
        if (!p) { toast.error("Choose a preset first"); return; }
        modulesTouchedRef.current = true;
        setForm((f) => ({ ...f, portal_modules: [...p.module_ids], portal_module_mode: "preset" }));
        toast.success(`Applied "${p.name}" — ${p.module_ids.length} modules. You can still override below.`);
    }
    const labelFor = (id) => {
        for (const g of catalog?.groups || []) for (const m of g.modules) if (m.id === id) return m.label;
        return id;
    };
    const selectedPreset = presets.find((x) => x.id === form.portal_preset_id) || null;

    function onNameChange(value) {
        setForm((f) => {
            const next = { ...f, name: value };
            // Auto-derive logo initials from name only for new clients (no existing id)
            // and only if the user hasn't typed their own initials yet.
            if (!initial?.id && value && !f.branding.logo_initials) {
                const ini = value.split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase();
                next.branding = { ...f.branding, logo_initials: ini };
            }
            return next;
        });
    }

    return (
        <div className="space-y-5 max-h-[68vh] overflow-y-auto pr-2">
            {/* Identity block */}
            <Section title="Identity">
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Client name" required>
                        <input data-testid="client-form-name"
                            value={form.name} onChange={(e) => onNameChange(e.target.value)}
                            placeholder="e.g. Properties Living"
                            className="form-input" />
                    </Field>
                    <Field label="Trading name">
                        <input data-testid="client-form-trading"
                            value={form.trading_name || ""} onChange={(e) => set("trading_name", e.target.value)}
                            placeholder="Ltd / LLP / PLC"
                            className="form-input" />
                    </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Sector">
                        <select data-testid="client-form-sector"
                            value={form.sector || ""} onChange={(e) => set("sector", e.target.value)}
                            className="form-input">
                            <option value="">Select…</option>
                            {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Account manager">
                        <input data-testid="client-form-manager"
                            value={form.account_manager || ""} onChange={(e) => set("account_manager", e.target.value)}
                            placeholder="Internal owner"
                            className="form-input" />
                    </Field>
                </div>
            </Section>

            {/* Contact block */}
            <Section title="Primary contact">
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Main contact">
                        <input data-testid="client-form-contact"
                            value={form.main_contact || ""} onChange={(e) => set("main_contact", e.target.value)}
                            placeholder="Full name" className="form-input" />
                    </Field>
                    <Field label="Email">
                        <input data-testid="client-form-email" type="email"
                            value={form.email || ""} onChange={(e) => set("email", e.target.value)}
                            placeholder="contact@client.co.uk" className="form-input" />
                    </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Telephone">
                        <input data-testid="client-form-tel"
                            value={form.telephone || ""} onChange={(e) => set("telephone", e.target.value)}
                            placeholder="020 1234 5678" className="form-input" />
                    </Field>
                    <Field label="Website">
                        <input data-testid="client-form-web"
                            value={form.website || ""} onChange={(e) => set("website", e.target.value)}
                            placeholder="https://…" className="form-input" />
                    </Field>
                </div>
                <Field label="Address">
                    <textarea data-testid="client-form-address" rows={2}
                        value={form.address || ""} onChange={(e) => set("address", e.target.value)}
                        placeholder="Postal address" className="form-input" />
                </Field>
            </Section>

            {/* Portal branding */}
            <Section title="Portal branding" icon={Palette}>
                <BrandPreview clientId={initial?.id} branding={form.branding} setBrand={setBrand} onPendingLogo={onPendingLogo} />
                <div className="flex flex-wrap gap-2 mt-3">
                    {BRAND_PRESETS.map((c) => (
                        <button key={c} type="button"
                            onClick={() => setBrand("primary_colour", c)}
                            data-testid={`client-form-preset-${c.replace("#", "")}`}
                            title={c}
                            className="w-7 h-7 rounded-md border-2 transition-transform hover:scale-110"
                            style={{
                                background: c,
                                borderColor: form.branding.primary_colour === c ? "#1F1F1F" : "transparent",
                            }} />
                    ))}
                </div>
                {initial?.id && (
                    <>
                        <CoverUploader clientId={initial.id} branding={form.branding} setBrand={setBrand} />
                        <PortalBrandingControls clientId={initial.id} branding={form.branding}
                            onSaved={(b) => {
                                setBrand("heading_font", b?.heading_font ?? null);
                                setBrand("body_font", b?.body_font ?? null);
                                setBrand("banner_url", b?.banner_url ?? null);
                                setBrand("wide_logo_url", b?.wide_logo_url ?? null);
                            }} />
                    </>
                )}
            </Section>

            {/* Portal access */}
            <Section title="Portal access" icon={Globe2}>
                <Toggle
                    testId="client-form-portal"
                    icon={Globe2}
                    label="Portal enabled"
                    sub="Allow this client's users to sign in to the branded portal."
                    value={form.portal_enabled} onChange={(v) => set("portal_enabled", v)} />
                <Toggle
                    testId="client-form-hide-financials"
                    icon={PoundSterling}
                    label="Hide financial values in portal"
                    sub="Suppress £ project values and other monetary figures for this client's portal users."
                    value={form.hide_financials || false} onChange={(v) => set("hide_financials", v)} />
                <Toggle
                    testId="client-form-pulse-digest"
                    icon={Activity}
                    label="Weekly Pulse email digest"
                    sub="Send this client a weekly emailed read-out (reports, events, sign-ins, actions, calendar). Off by default."
                    value={form.pulse_digest_enabled || false} onChange={(v) => set("pulse_digest_enabled", v)} />
                <Toggle
                    testId="client-form-weekly-reports"
                    icon={ClipboardCheck}
                    label="Weekly Site Reports"
                    sub="Let this client's Site Managers submit a weekly site report per project, with a Client Admin dashboard. Off by default."
                    value={form.weekly_reports_enabled || false} onChange={(v) => set("weekly_reports_enabled", v)} />
                <Toggle
                    testId="client-form-equipment-register"
                    icon={Wrench}
                    label="Equipment on Site register"
                    sub="Track plant/equipment, LOLER & PUWER checks per project — feeds Weekly Site Report 5.0. Auto-enabled with Weekly Site Reports."
                    value={form.equipment_register_enabled || false} onChange={(v) => set("equipment_register_enabled", v)} />
                {form.weekly_reports_enabled && (
                    <>
                        <Toggle
                            testId="client-form-wsr-notifications"
                            icon={BellRing}
                            label="WSR notifications"
                            sub="Notify nominated WSR reviewers when a report is submitted, and the Site Manager when it's returned or accepted. Off by default."
                            value={form.wsr_notifications_enabled || false} onChange={(v) => set("wsr_notifications_enabled", v)} />
                        <Toggle
                            testId="client-form-wsr-reminders"
                            icon={CalendarClock}
                            label="WSR reminders"
                            sub="Reminder emails to Site Managers whose report isn't submitted yet, building up to the due time below."
                            value={form.wsr_reminders_enabled || false} onChange={(v) => set("wsr_reminders_enabled", v)} />
                        <div className="grid sm:grid-cols-2 gap-3 pl-1">
                            <label className="block">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>WSR due day</span>
                                <select data-testid="client-form-wsr-due-weekday" value={form.wsr_due_weekday ?? 4}
                                    onChange={(e) => set("wsr_due_weekday", Number(e.target.value))}
                                    className="w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }}>
                                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                                        <option key={d} value={i}>{d}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>WSR due time (Europe/London)</span>
                                <input data-testid="client-form-wsr-due-time" type="time" value={form.wsr_due_time || "14:00"}
                                    onChange={(e) => set("wsr_due_time", e.target.value)}
                                    className="w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                            </label>
                        </div>
                        {initial?.id && (
                            <div className="pl-1">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>WSR reviewers — first to accept/return closes it for everyone else. Empty = every Client Admin for this client.</span>
                                <WsrReviewerPicker clientId={initial.id} value={form.wsr_reviewer_user_ids || []} onChange={(v) => set("wsr_reviewer_user_ids", v)} />
                            </div>
                        )}
                        {initial?.id && (
                            <div className="pl-1">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Management summary reviewers — any one can comment/finalise; first to finalise closes it for everyone else. Empty = every Client Admin for this client.</span>
                                <WsrReviewerPicker clientId={initial.id} value={form.ms_reviewer_user_ids || []} onChange={(v) => set("ms_reviewer_user_ids", v)}
                                    roleFilter={["client_admin"]} testIdPrefix="ms-reviewer-option" emptyLabel="No Client Admins found for this client yet." />
                            </div>
                        )}
                        {initial?.id && (
                            <div className="pl-1">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Weekly Management Summary go-live guard — the job never builds, releases, emails or marks "Late" any week before this one. Leave blank for no restriction.</span>
                                <input data-testid="client-form-ms-start-week" type="date" value={msStartWeekDisplay(form.ms_start_week)}
                                    onChange={(e) => set("ms_start_week", weekCommencingSat(e.target.value))}
                                    className="h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                                {form.ms_start_week && (
                                    <span className="ml-2 text-[11px]" style={{ color: "#8B867E" }}>Week commencing {form.ms_start_week}</span>
                                )}
                            </div>
                        )}
                    </>
                )}
            </Section>

            {/* Module configuration */}
            <Section title="Module configuration" icon={SlidersHorizontal}>
                <p className="text-[11.5px] -mt-1 mb-1" style={{ color: "#6F6A64" }}>
                    Choose which modules this client's portal shows. Disabled modules are hidden from their menu and dashboard.
                </p>
                <div className="flex gap-2" data-testid="module-mode-switch">
                    <ModeButton active={form.portal_module_mode === "preset"} onClick={() => setModuleMode("preset")}
                        testId="module-mode-preset" icon={Wand2} label="Use preset" sub="Apply a saved bundle" />
                    <ModeButton active={form.portal_module_mode === "custom"} onClick={() => setModuleMode("custom")}
                        testId="module-mode-custom" icon={SlidersHorizontal} label="Custom modules" sub="Pick manually" />
                </div>

                {form.portal_module_mode === "preset" && (
                    <div className="space-y-3 mt-1" data-testid="module-preset-block">
                        <div className="flex items-end gap-2">
                            <label className="flex-1">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>Preset</span>
                                <select data-testid="module-preset-select" value={form.portal_preset_id || ""}
                                    onChange={(e) => set("portal_preset_id", e.target.value || null)} className="form-input">
                                    <option value="">Select a preset…</option>
                                    {presets.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_default ? " (default)" : ""}</option>)}
                                </select>
                            </label>
                            <button type="button" onClick={applyPreset} disabled={!form.portal_preset_id}
                                data-testid="module-apply-preset"
                                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                <Wand2 size={13} /> Apply preset
                            </button>
                        </div>
                        {selectedPreset && (
                            <div className="rounded-lg border p-3" style={{ borderColor: "#E5E0DA", background: "#FBFAF8" }} data-testid="module-preset-preview">
                                <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#9A968F] mb-1.5">Preset includes</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {selectedPreset.module_ids.map((id) => (
                                        <span key={id} className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBEFE5] text-[#9F4212] font-medium">{labelFor(id)}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <p className="text-[11px]" style={{ color: "#9A968F" }}>Applying a preset overwrites the modules below — you can then fine-tune them per client.</p>
                    </div>
                )}

                {catalog ? (
                    <div className="mt-2">
                        <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#9A968F] mb-2">
                            {form.portal_module_mode === "preset" ? "Enabled modules (override)" : "Enabled modules"}
                        </div>
                        <PortalModuleSelector catalog={catalog} value={form.portal_modules || []} onChange={setModules} idPrefix="client-module" />
                    </div>
                ) : (
                    <div className="text-[12px] text-[#9A968F] py-2"><Loader2 size={12} className="inline animate-spin mr-1.5" />Loading modules…</div>
                )}
            </Section>

            {/* Risk + Status */}
            <Section title="Risk & status">
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Risk rating">
                        <select data-testid="client-form-risk"
                            value={form.risk} onChange={(e) => set("risk", e.target.value)}
                            className="form-input">
                            <option>Low</option><option>Medium</option><option>High</option>
                        </select>
                    </Field>
                    <Field label="Status">
                        <select data-testid="client-form-status"
                            value={form.status} onChange={(e) => set("status", e.target.value)}
                            className="form-input">
                            <option>Active</option><option>Onboarding</option><option>Paused</option><option>Archived</option>
                        </select>
                    </Field>
                </div>
                <Field label="Internal notes">
                    <textarea data-testid="client-form-notes" rows={2}
                        value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
                        placeholder="Optional · visible to Priority Risk staff only" className="form-input" />
                </Field>
            </Section>

            {/* Linked contacts (edit mode only — needs a saved client to attach to) */}
            {initial?.id && (
                <Section title="Contacts" icon={Users}>
                    <ClientContactsList clientId={initial.id} />
                </Section>
            )}

            <style>{`
                .form-input {
                    width: 100%;
                    padding: 8px 11px;
                    background: #fff;
                    border: 1px solid #E5E0DA;
                    border-radius: 8px;
                    font-size: 12.5px;
                    color: #1F1F1F;
                    outline: none;
                    transition: border-color .12s, box-shadow .12s;
                }
                .form-input:focus {
                    border-color: var(--brand-accent, #C75A1B);
                    box-shadow: 0 0 0 3px #FBEFE5;
                }
            `}</style>
        </div>
    );
}

function CoverUploader({ clientId, branding, setBrand }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const hasCover = !!branding.cover_pdf_path;

    async function upload(file) {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Upload a PDF cover page"); return; }
        if (file.size > 8 * 1024 * 1024) { toast.error("Cover must be under 8 MB"); return; }
        setBusy(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post(`/clients/${clientId}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setBrand("cover_pdf_path", data.branding?.cover_pdf_path);
            setBrand("cover_pdf_name", data.branding?.cover_pdf_name);
            setBrand("cover_enabled", data.branding?.cover_enabled ?? true);
            toast.success("Cover page uploaded");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
        setBusy(false);
    }
    async function remove() {
        if (!window.confirm("Remove the uploaded cover page? A generated cover will be used instead.")) return;
        setBusy(true);
        try {
            await api.delete(`/clients/${clientId}/cover`);
            setBrand("cover_pdf_path", null); setBrand("cover_pdf_name", null);
            toast.success("Cover removed — a branded cover will be generated automatically");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
        setBusy(false);
    }
    async function toggle(enabled) {
        setBrand("cover_enabled", enabled);
        try { await api.patch(`/clients/${clientId}/cover-enabled`, { enabled }); }
        catch (e) { toast.error("Could not update setting"); }
    }

    return (
        <div className="mt-4 rounded-[10px] border p-3" style={{ borderColor: "#E5E0DA", background: "#FBFAF8" }} data-testid="client-cover-uploader">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mb-1">Document cover page</label>
            <p className="text-[11px] mb-2 text-[#6F6A64]">Prepended to generated reports (inspections, TMP, CLP, BSCR) and PDF documents. The document title, version, date & project are overlaid automatically. Leave empty to auto-generate a branded cover.</p>
            <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                    data-testid="client-cover-upload"
                    className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F5F3F0] disabled:opacity-50"
                    style={{ borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    {hasCover ? "Replace cover PDF" : "Upload cover PDF"}
                </button>
                {hasCover && (
                    <>
                        <span className="text-[11.5px] text-[#3F7D3A] font-semibold truncate max-w-[180px]" data-testid="client-cover-name">✓ {branding.cover_pdf_name || "cover.pdf"}</span>
                        <button type="button" onClick={remove} disabled={busy} data-testid="client-cover-remove"
                            className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-lg text-red-600 hover:bg-red-50">
                            <Trash2 size={12} /> Remove
                        </button>
                    </>
                )}
                <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden"
                    onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            </div>
            <label className="flex items-center gap-2 mt-2.5 text-[12px] font-semibold cursor-pointer text-[#1F1F1F]" data-testid="client-cover-enabled-toggle">
                <input type="checkbox" checked={branding.cover_enabled !== false} onChange={(e) => toggle(e.target.checked)} />
                Add the cover page when generating / downloading PDFs
            </label>
        </div>
    );
}

function ModeButton({ active, onClick, testId, icon: Icon, label, sub }) {
    return (
        <button type="button" onClick={onClick} data-testid={testId}
            className={`flex-1 flex items-start gap-2.5 p-3 rounded-[10px] border text-left transition-colors ${active
                ? "bg-[#FBEFE5] border-[var(--brand-accent,_#C75A1B)]"
                : "bg-white border-[#E5E0DA] hover:border-[#9A968F]"}`}>
            <div className="w-8 h-8 rounded-[8px] grid place-items-center flex-shrink-0"
                style={{ background: active ? "var(--brand-accent, #C75A1B)" : "#F0EDE8", color: active ? "#fff" : "#6F6A64" }}>
                <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
                <div className="text-[12.5px] font-semibold text-[#1F1F1F]">{label}</div>
                <div className="text-[11px] mt-0.5" style={{ color: "#6F6A64" }}>{sub}</div>
            </div>
        </button>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-[.08em] font-bold mb-2.5 flex items-center gap-1.5"
                 style={{ color: "#9A968F" }}>
                {Icon ? <Icon className="w-3 h-3" /> : null}
                {title}
            </div>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>
                {label} {required ? <span className="text-[#B33A2E]">*</span> : null}
            </span>
            {children}
        </label>
    );
}

export function WsrReviewerPicker({ clientId, value, onChange, roleFilter, testIdPrefix = "wsr-reviewer-option", emptyLabel = "No PR staff or client users found yet." }) {
    const [users, setUsers] = useState(null);
    useEffect(() => {
        api.get("/users").then((r) => {
            setUsers((r.data || []).filter((u) => {
                const inClient = u.client_id === clientId || (u.client_ids || []).includes(clientId);
                if (roleFilter) return inClient && roleFilter.includes(u.role);
                return ["director", "administrator"].includes(u.role) || inClient;
            }));
        }).catch(() => setUsers([]));
    }, [clientId, roleFilter]);
    const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
    if (users === null) return <div className="text-[12px] text-[#9A968F]"><Loader2 size={12} className="inline animate-spin mr-1" />Loading users…</div>;
    return (
        <div className="border rounded-lg divide-y max-h-44 overflow-y-auto" style={{ borderColor: "#E5E0DA" }}>
            {users.length === 0 && <div className="px-3 py-2 text-[12.5px] text-[#9A968F]">{emptyLabel}</div>}
            {users.map((u) => (
                <label key={u.id} data-testid={`${testIdPrefix}-${u.id}`} className="flex items-center gap-2 px-3 py-2 text-[13px] cursor-pointer hover:bg-[#F6F4F0]">
                    <input type="checkbox" checked={value.includes(u.id)} onChange={() => toggle(u.id)} />
                    <span className="flex-1">{u.name} <span className="text-[11px]" style={{ color: "#9A968F" }}>· {u.role.replace("_", " ")} · {u.email}</span></span>
                </label>
            ))}
        </div>
    );
}

function Toggle({ testId, icon: Icon, label, sub, value, onChange }) {
    return (
        <button type="button"
            data-testid={testId}
            role="switch"
            aria-checked={value}
            onClick={() => onChange(!value)}
            className={`w-full flex items-center gap-3 p-3 rounded-[10px] border text-left transition-colors ${value
                    ? "bg-[#FBEFE5] border-[var(--brand-accent,_#C75A1B)]"
                    : "bg-white border-[#E5E0DA] hover:border-[#9A968F]"
                }`}>
            <div className="w-9 h-9 rounded-[8px] grid place-items-center flex-shrink-0"
                 style={{
                     background: value ? "var(--brand-accent, #C75A1B)" : "#F0EDE8",
                     color: value ? "#fff" : "#6F6A64",
                 }}>
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold text-[#1F1F1F]">{label}</div>
                <div className="text-[11px] mt-0.5" style={{ color: "#6F6A64" }}>{sub}</div>
            </div>
            <div className="w-9 h-5 rounded-full relative transition-colors flex-shrink-0"
                 style={{ background: value ? "#3F7D3A" : "#D8D3CB" }}>
                <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all"
                      style={{ left: value ? "18px" : "2px" }} />
            </div>
        </button>
    );
}

const LOGO_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];

function BrandPreview({ clientId, branding, setBrand, onPendingLogo }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [bust, setBust] = useState(Date.now());
    const [logoBroken, setLogoBroken] = useState(false);
    const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
    const remoteLogoUrl = branding.logo_url && !logoBroken
        ? `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/logo?t=${bust}`
        : null;
    // While the client hasn't been created yet there's no id to upload against —
    // show a local object-URL preview instead and hand the raw File up to the
    // dialog, which uploads it right after the client record is created.
    const logoUrl = clientId ? remoteLogoUrl : pendingPreviewUrl;
    const previewBg = branding.secondary_colour
        ? `linear-gradient(135deg, ${branding.primary_colour} 0%, ${branding.secondary_colour} 100%)`
        : branding.primary_colour;

    async function uploadLogo(file) {
        if (!file) return;
        if (!LOGO_MIME_TYPES.includes(file.type)) { toast.error("Unsupported image type — use PNG, JPG, WebP or SVG"); return; }
        if (file.size > 4 * 1024 * 1024) { toast.error("Logo must be under 4 MB"); return; }

        if (!clientId) {
            // New client: stage the file locally until it's saved.
            if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
            setPendingPreviewUrl(URL.createObjectURL(file));
            onPendingLogo?.(file);
            return;
        }

        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/clients/${clientId}/upload-logo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setBrand("logo_url", data.branding?.logo_url);
            setBust(Date.now());
            setLogoBroken(false);
            toast.success("Logo uploaded");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
        }
        setBusy(false);
    }

    async function removeLogo() {
        if (!clientId) {
            if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
            setPendingPreviewUrl(null);
            onPendingLogo?.(null);
            return;
        }
        if (!window.confirm("Remove uploaded logo and fall back to initials?")) return;
        setBusy(true);
        try {
            await api.delete(`/clients/${clientId}/logo`);
            setBrand("logo_url", null);
            toast.success("Logo removed");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
        setBusy(false);
    }

    return (
        <div className="space-y-4">
        <div className="flex items-end gap-4">
            <div
                className="w-20 h-20 rounded-[14px] grid place-items-center font-serif text-3xl text-white shadow-brand overflow-hidden relative"
                style={{ background: previewBg }}
            >
                {logoUrl ? (
                    <img src={logoUrl} alt="logo" data-testid="client-form-logo-preview"
                        onError={() => setLogoBroken(true)}
                        className="absolute inset-0 w-full h-full object-contain bg-white" />
                ) : (branding.logo_initials || "—")}
            </div>
            <div className="flex-1 space-y-3">
                <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mb-1">Logo image</label>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                            data-testid="client-form-logo-upload"
                            className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F5F3F0] disabled:opacity-50"
                            style={{ borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                            {logoUrl ? "Replace logo" : "Upload logo"}
                        </button>
                        {logoUrl && (
                            <button type="button" onClick={removeLogo} disabled={busy}
                                data-testid="client-form-logo-remove"
                                className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg text-red-600 hover:bg-red-50">
                                <Trash2 size={12} /> Remove
                            </button>
                        )}
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            className="hidden" onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
                    </div>
                    <p className="text-[11px] mt-1 text-[#9A968F]">
                        {clientId
                            ? "PNG / JPG / WebP / SVG · max 4 MB. Leave empty to use initials."
                            : "PNG / JPG / WebP / SVG · max 4 MB. Uploaded automatically once you create the client."}
                    </p>
                </div>
                <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mb-1">Logo initials (max 3)</label>
                    <input data-testid="client-form-initials"
                        value={branding.logo_initials || ""}
                        onChange={(e) => setBrand("logo_initials", e.target.value.toUpperCase().slice(0, 3))}
                        maxLength={3} placeholder="PL" className="form-input uppercase" />
                </div>
                <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mb-1">Primary brand colour</label>
                    <div className="flex items-center gap-2">
                        <input data-testid="client-form-colour" type="color"
                            value={branding.primary_colour}
                            onChange={(e) => setBrand("primary_colour", e.target.value)}
                            className="w-10 h-10 rounded-md border border-[#E5E0DA] cursor-pointer p-0" />
                        <input value={branding.primary_colour}
                            onChange={(e) => setBrand("primary_colour", e.target.value)}
                            className="form-input font-mono uppercase text-[12px] flex-1" />
                    </div>
                </div>
                <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mb-1">
                        Secondary brand colour <span className="font-normal normal-case tracking-normal">(optional · used for gradients)</span>
                    </label>
                    <div className="flex items-center gap-2">
                        <input data-testid="client-form-secondary-colour" type="color"
                            value={branding.secondary_colour || branding.primary_colour}
                            onChange={(e) => setBrand("secondary_colour", e.target.value)}
                            className="w-10 h-10 rounded-md border border-[#E5E0DA] cursor-pointer p-0" />
                        <input value={branding.secondary_colour || ""}
                            placeholder="Leave empty for solid colour"
                            onChange={(e) => setBrand("secondary_colour", e.target.value || null)}
                            className="form-input font-mono uppercase text-[12px] flex-1" />
                        {branding.secondary_colour && (
                            <button type="button" onClick={() => setBrand("secondary_colour", null)}
                                data-testid="client-form-secondary-clear"
                                className="text-[11px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F] px-2 py-1 rounded hover:bg-[#F6F4F0]">
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
            <PortalThemePreview branding={branding} />
        </div>
    );
}

// Live mini-mock of how the client's branding renders in their portal
// (primary owns the nav + hero, secondary appears only as accents).
function PortalThemePreview({ branding }) {
    const primary = branding.primary_colour || "#1B3A5C";
    const secondary = branding.secondary_colour || null;
    const heroEnd = shade(primary, -0.18);
    const activeBg = secondary ? "rgba(255,255,255,.20)" : heroEnd;
    return (
        <div data-testid="portal-theme-preview">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mb-1.5">
                Portal preview
            </label>
            <div className="rounded-xl overflow-hidden border flex shadow-sm" style={{ borderColor: "#E5E0DA", height: 138 }}>
                {/* mini sidebar */}
                <div className="w-[80px] flex-shrink-0 flex flex-col" style={{ background: primary }}>
                    <div className="h-7 bg-white grid place-items-center text-[9px] font-bold flex-shrink-0" style={{ color: primary }}>
                        {branding.logo_initials || "LOGO"}
                    </div>
                    <div className="p-1.5 space-y-1">
                        <div className="h-3.5 rounded flex items-center px-1.5 text-[6.5px] font-semibold text-white"
                            style={{ background: activeBg, borderLeft: secondary ? `3px solid ${secondary}` : "none" }}>
                            Dashboard
                        </div>
                        {["Sites", "Reports", "Actions"].map((t) => (
                            <div key={t} className="h-3.5 rounded flex items-center px-1.5 text-[6.5px]"
                                style={{ color: "rgba(255,255,255,.8)" }}>{t}</div>
                        ))}
                    </div>
                </div>
                {/* mini content + hero */}
                <div className="flex-1 min-w-0" style={{ background: "#F5F3F0" }}>
                    <div className="px-2.5 py-2 text-white"
                        style={{
                            background: `linear-gradient(135deg, ${primary} 0%, ${heroEnd} 100%)`,
                            borderBottom: secondary ? `2px solid ${secondary}` : "none",
                        }}>
                        <div className="text-[6px] uppercase tracking-[.2em]" style={{ color: secondary || "rgba(255,255,255,.85)" }}>
                            Client portal
                        </div>
                        <div className="h-2 w-20 rounded-full mt-1 bg-white/85" />
                    </div>
                    <div className="p-2 grid grid-cols-3 gap-1.5">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="rounded-md bg-white border p-1.5" style={{ borderColor: "#E5E0DA" }}>
                                <div className="w-3 h-3 rounded grid place-items-center mb-1"
                                    style={{ background: `${primary}1A` }}>
                                    <div className="w-1.5 h-1.5 rounded-sm" style={{ background: primary }} />
                                </div>
                                <div className="h-1.5 w-5 rounded-full" style={{ background: "#E5E0DA" }} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <p className="text-[11px] mt-1 text-[#9A968F]">
                {secondary
                    ? "Primary colour owns the navigation and header; secondary appears as accents only."
                    : "Add a secondary colour to introduce accent highlights."}
            </p>
        </div>
    );
}

function ClientContactsList({ clientId }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [newForm, setNewForm] = useState({ name: "", role: "", email: "", phone: "", portal_access: false, is_primary: false });

    async function refresh() {
        setLoading(true);
        try {
            const { data } = await api.get("/contacts", { params: { client_id: clientId } });
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load contacts");
        }
        setLoading(false);
    }
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [clientId]);

    async function save() {
        if (!newForm.name.trim()) { toast.error("Name required"); return; }
        try {
            const payload = { client_id: clientId, ...newForm };
            Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
            await api.post("/contacts", payload);
            toast.success(`Added ${newForm.name}`);
            setNewForm({ name: "", role: "", email: "", phone: "", portal_access: false, is_primary: false });
            setAdding(false);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
    }

    async function togglePortal(c) {
        try {
            await api.patch(`/contacts/${c.id}`, { portal_access: !c.portal_access });
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
        }
    }

    async function removeContact(c) {
        if (!window.confirm(`Remove ${c.name}?`)) return;
        try {
            await api.delete(`/contacts/${c.id}`);
            toast.success("Contact removed");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    }

    return (
        <div data-testid="client-contacts-list">
            {loading ? (
                <div className="text-[12px] text-[#9A968F] py-2">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="text-[12px] text-[#9A968F] py-2">No contacts linked yet.</div>
            ) : (
                <table className="w-full text-[12.5px] mb-3">
                    <thead className="text-[10px] uppercase tracking-wider text-[#9A968F] font-bold">
                        <tr>
                            <th className="text-left py-1">Name</th><th className="text-left py-1">Role</th>
                            <th className="text-left py-1">Email / Phone</th><th className="text-center py-1">Portal</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c) => (
                            <tr key={c.id} data-testid={`client-contact-row-${c.id}`} className="border-t border-[#F0EDE8]">
                                <td className="py-2 font-semibold text-[#1F1F1F]">{c.name}
                                    {c.is_primary && <span className="ml-1.5 text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-[#FBEFE5] text-[#9F4212]">Primary</span>}
                                </td>
                                <td className="py-2 text-[#6F6A64]">{c.role || "—"}</td>
                                <td className="py-2 text-[#6F6A64]">
                                    <div>{c.email || "—"}</div>
                                    <div className="text-[11px]">{c.phone || ""}</div>
                                </td>
                                <td className="py-2 text-center">
                                    <button onClick={() => togglePortal(c)} type="button"
                                        data-testid={`client-contact-portal-${c.id}`}
                                        className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${c.portal_access ? "bg-[#E8F5E9] text-[#3F7D3A]" : "bg-[#F0EDE8] text-[#9A968F]"}`}>
                                        {c.portal_access ? "Yes" : "No"}
                                    </button>
                                </td>
                                <td className="py-2 text-right">
                                    <button onClick={() => removeContact(c)} type="button"
                                        data-testid={`client-contact-delete-${c.id}`}
                                        className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={12} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {adding ? (
                <div className="bg-[#F5F3F0] rounded-lg p-3 space-y-2" data-testid="client-contact-add-form">
                    <div className="grid grid-cols-2 gap-2">
                        <input placeholder="Name *" value={newForm.name}
                            data-testid="client-contact-name"
                            onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} className="form-input" />
                        <input placeholder="Role / title" value={newForm.role}
                            onChange={(e) => setNewForm({ ...newForm, role: e.target.value })} className="form-input" />
                        <input placeholder="Email" type="email" value={newForm.email}
                            onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} className="form-input" />
                        <input placeholder="Phone" value={newForm.phone}
                            onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })} className="form-input" />
                    </div>
                    <div className="flex items-center gap-4 text-[12px]">
                        <label className="flex items-center gap-1"><input type="checkbox" checked={newForm.portal_access}
                            onChange={(e) => setNewForm({ ...newForm, portal_access: e.target.checked })} /> Portal access</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={newForm.is_primary}
                            onChange={(e) => setNewForm({ ...newForm, is_primary: e.target.checked })} /> Primary</label>
                        <div className="ml-auto flex items-center gap-1.5">
                            <button type="button" onClick={() => setAdding(false)} className="text-[12px] text-[#6F6A64] hover:text-[#1F1F1F] px-2 py-1">Cancel</button>
                            <button type="button" onClick={save} data-testid="client-contact-save"
                                className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                Add contact
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <button type="button" onClick={() => setAdding(true)}
                    data-testid="client-contact-new-btn"
                    className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F5F3F0]"
                    style={{ borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                    + Add contact
                </button>
            )}
        </div>
    );
}
